import { useState, useEffect } from "react";
import { motion } from "framer-motion";
import { useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";

import type { RootState } from "../../../store";
import Header from "../../../components/common/Header";
import Footer from "../../../components/common/Footer";
import BackToTop from "../../../components/common/BackToTop";
import PaymentModal from "../../../components/common/PaymentModal";
import { useProjects } from "../../../services/projectService";

const Projects = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [currentPage, setCurrentPage] = useState(1);
  const [isDataLoaded, setIsDataLoaded] = useState(false);
  const [isPaymentModalOpen, setIsPaymentModalOpen] = useState(false);
  const [selectedProject, setSelectedProject] = useState<{ id: number; title: string } | null>(null);
  const { currentLanguage } = useSelector((state: RootState) => state.language);

  const {
    data: projectsData,
    isLoading,
    error,
  } = useProjects({
    per_page: 12,
    page: currentPage,
    locale: currentLanguage,
  });

  const projects = projectsData?.projects || [];
  const pagination = projectsData?.pagination;

  // Trigger animations when data loads
  useEffect(() => {
    if (!isLoading && projects.length > 0) {
      setIsDataLoaded(true);
    } else if (!isLoading) {
      // Even if no projects, set loaded to show empty state
      setIsDataLoaded(true);
    }
  }, [isLoading, projects.length]);

  const calculateProgress = (current: number, target: number) => {
    return Math.min((current / target) * 100, 100);
  };

  return (
    <div className="min-h-screen w-full bg-gray-100 dark:bg-gray-900 text-gray-900 dark:text-gray-100 transition-colors">
      <Header />
      <main>
        {/* Hero Section */}
        <section className="relative min-h-[75vh] flex items-center overflow-hidden bg-gradient-to-br from-stone-600 via-emerald-600 to-stone-700 text-white">
          {/* Background decorative elements */}
          <div className="absolute inset-0 overflow-hidden pointer-events-none">
            <div className="absolute top-0 right-0 w-96 h-96 bg-white/10 rounded-full blur-3xl -translate-y-1/2 translate-x-1/2"></div>
            <div className="absolute bottom-0 left-0 w-96 h-96 bg-white/10 rounded-full blur-3xl translate-y-1/2 -translate-x-1/2"></div>
          </div>

          <div
            className="absolute inset-0 opacity-20"
            style={{
              backgroundImage: `url("data:image/svg+xml,%3Csvg width='60' height='60' viewBox='0 0 60 60' xmlns='http://www.w3.org/2000/svg'%3E%3Cg fill='none' fill-rule='evenodd'%3E%3Cg fill='%23ffffff' fill-opacity='0.05'%3E%3Cpath d='M36 34v-4h-2v4h-4v2h4v4h2v-4h4v-2h-4zm0-30V0h-2v4h-4v2h4v4h2V6h4V4h-4zM6 34v-4H4v4H0v2h4v4h2v-4h4v-2H6zM6 4V0H4v4H0v2h4v4h2V6h4V4H6z'/%3E%3C/g%3E%3C/g%3E%3C/svg%3E")`,
            }}
          ></div>

          <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 w-full py-24">
            <div className="max-w-4xl mx-auto text-center">
              <motion.div
                initial={{ opacity: 0, y: 30 }}
                animate={isDataLoaded ? { opacity: 1, y: 0 } : { opacity: 0, y: 30 }}
                transition={{ duration: 0.8 }}
                className="mb-8"
              >
                <span className="inline-flex items-center gap-2 px-4 py-2 bg-white/20 backdrop-blur-md text-white rounded-full text-sm font-semibold border border-white/30 shadow-lg">
                  <span className="text-lg">🚀</span>
                  <span>{t("projects.badge", "Community-Driven Projects")}</span>
                </span>
              </motion.div>

              <motion.h1
                initial={{ opacity: 0, y: 30 }}
                animate={isDataLoaded ? { opacity: 1, y: 0 } : { opacity: 0, y: 30 }}
                transition={{ duration: 0.8, delay: 0.2 }}
                className="text-4xl sm:text-5xl md:text-6xl lg:text-7xl font-extrabold mb-6 leading-tight tracking-tight"
              >
                {t("projects.title")}
                <span className="block mt-3 text-3xl sm:text-4xl md:text-5xl lg:text-6xl bg-gradient-to-r from-white via-emerald-100 to-stone-100 bg-clip-text text-transparent">
                  {t("projects.titleAccent", "Making an Impact")}
                </span>
              </motion.h1>

              <motion.p
                initial={{ opacity: 0, y: 20 }}
                animate={isDataLoaded ? { opacity: 1, y: 0 } : { opacity: 0, y: 20 }}
                transition={{ duration: 0.8, delay: 0.4 }}
                className="text-lg md:text-xl lg:text-2xl opacity-95 font-light max-w-3xl mx-auto leading-relaxed mb-12"
              >
                {t("projects.subtitle")}
              </motion.p>

              <motion.div
                initial={{ opacity: 0, scale: 0.9 }}
                animate={isDataLoaded ? { opacity: 1, scale: 1 } : { opacity: 0, scale: 0.9 }}
                transition={{ duration: 0.6, delay: 0.6 }}
                className="inline-block"
              >
                <div className="bg-white/25 backdrop-blur-md rounded-2xl px-8 py-4 border border-white/30 shadow-xl">
                  <span className="text-xl md:text-2xl font-bold">
                    {isLoading
                      ? t("projects.loading", "Loading...")
                      : `${pagination?.total || 0} ${t("projects.activeProjects", "Active Community Projects")}`}
                  </span>
                </div>
              </motion.div>
            </div>
          </div>
        </section>

        {/* Projects Grid */}
        <section className="relative py-24 px-4 sm:px-6 lg:px-8 overflow-hidden bg-gradient-to-br from-stone-50/30 via-emerald-50/30 to-stone-50/30 dark:from-gray-900 dark:via-gray-800 dark:to-gray-900">
          {/* Background decorative elements */}
          <div className="absolute inset-0 overflow-hidden pointer-events-none">
            <div className="absolute top-0 right-0 w-96 h-96 bg-stone-200/20 dark:bg-stone-900/10 rounded-full blur-3xl -translate-y-1/2 translate-x-1/2"></div>
            <div className="absolute bottom-0 left-0 w-96 h-96 bg-emerald-200/20 dark:bg-emerald-900/10 rounded-full blur-3xl translate-y-1/2 -translate-x-1/2"></div>
          </div>

          <div className="relative z-10 max-w-7xl mx-auto">
            {isLoading ? (
              <div className="flex flex-col justify-center items-center py-24">
                <div className="animate-spin rounded-full h-16 w-16 border-b-4 border-emerald-600 mb-6"></div>
                <p className="text-lg text-gray-600 dark:text-gray-400">
                  {t("projects.loading", "Loading projects...")}
                </p>
              </div>
            ) : error ? (
              <div className="text-center py-24">
                <motion.div
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.6 }}
                  className="max-w-2xl mx-auto"
                >
                  <div className="mb-6">
                    <div className="inline-flex items-center justify-center w-20 h-20 bg-red-100 dark:bg-red-900/30 rounded-full mb-4">
                      <svg
                        className="w-10 h-10 text-red-600 dark:text-red-400"
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L3.732 16.5c-.77.833.192 2.5 1.732 2.5z"
                        />
                      </svg>
                    </div>
                    <h3 className="text-2xl font-bold text-gray-900 dark:text-white mb-2">
                      {t("projects.errorTitle", "Failed to Load Projects")}
                    </h3>
                  </div>
                  <p className="text-lg text-gray-600 dark:text-gray-400 mb-8">
                    {t("projects.errorLoading")}
                  </p>
                  <motion.button
                    whileHover={{ scale: 1.05, y: -2 }}
                    whileTap={{ scale: 0.95 }}
                    onClick={() => window.location.reload()}
                    className="inline-flex items-center gap-2 px-6 py-3 bg-gradient-to-r from-amber-500 to-orange-600 hover:from-amber-600 hover:to-orange-700 text-white rounded-xl font-semibold transition-all duration-300 shadow-lg hover:shadow-xl"
                  >
                    {t("projects.retry", "Try Again")}
                  </motion.button>
                </motion.div>
              </div>
            ) : projects.length === 0 ? (
              <div className="text-center py-24">
                <motion.div
                  initial={{ opacity: 0, y: 20 }}
                  animate={isDataLoaded ? { opacity: 1, y: 0 } : { opacity: 0, y: 20 }}
                  transition={{ duration: 0.6 }}
                  className="max-w-2xl mx-auto"
                >
                  <div className="mb-6">
                    <div className="inline-flex items-center justify-center w-24 h-24 bg-gray-100 dark:bg-gray-800 rounded-full mb-4">
                      <svg
                        className="w-12 h-12 text-gray-400 dark:text-gray-500"
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={1.5}
                          d="M19 11H5m14 0a2 2 0 012 2v6a2 2 0 01-2 2H5a2 2 0 01-2-2v-6a2 2 0 012-2m14 0V9a2 2 0 00-2-2M5 11V9a2 2 0 012-2m0 0V5a2 2 0 012-2h6a2 2 0 012 2v2M7 7h10"
                        />
                      </svg>
                    </div>
                    <h3 className="text-3xl font-bold text-gray-900 dark:text-white mb-4">
                      {t("projects.noProjectsTitle")}
                    </h3>
                  </div>
                  <p className="text-lg text-gray-600 dark:text-gray-400 mb-8 leading-relaxed">
                    {t("projects.noProjects")}
                  </p>
                  <motion.button
                    whileHover={{ scale: 1.05, y: -2 }}
                    whileTap={{ scale: 0.95 }}
                    onClick={() => window.scrollTo({ top: 0, behavior: "smooth" })}
                    className="inline-flex items-center gap-2 px-6 py-3 bg-gradient-to-r from-emerald-600 to-stone-600 hover:from-emerald-700 hover:to-stone-700 text-white rounded-xl font-semibold transition-all duration-300 shadow-lg hover:shadow-xl"
                  >
                    <svg
                      className="w-5 h-5"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M5 10l7-7m0 0l7 7m-7-7v18"
                      />
                    </svg>
                    {t("projects.backToTop", "Back to Top")}
                  </motion.button>
                </motion.div>
              </div>
            ) : (
              <>
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8 lg:gap-10">
                  {projects.map((project, index) => {
                    const progressPercentage = calculateProgress(
                      project.raised_amount,
                      project.goal_amount
                    );
                    const remaining = project.goal_amount - project.raised_amount;
                    const translation = project.translations?.[0];
                    const image = project.hero_image || project.media?.[0]?.path || "/placeholder-project.jpg";

                    return (
                      <motion.div
                        key={project.id}
                        initial={{ opacity: 0, y: 50 }}
                        animate={isDataLoaded ? { opacity: 1, y: 0 } : { opacity: 0, y: 50 }}
                        transition={{ duration: 0.6, delay: index * 0.1 }}
                        whileHover={{ y: -12, scale: 1.02 }}
                        className="group relative"
                      >
                        {/* Floating gradient glow */}
                        <div className="absolute -inset-1 bg-gradient-to-r from-stone-500 via-emerald-500 to-stone-600 rounded-3xl opacity-0 group-hover:opacity-30 blur-xl transition-opacity duration-500"></div>

                        {/* Main Card */}
                        <div className="relative bg-white dark:bg-gray-800 rounded-3xl shadow-xl hover:shadow-2xl transition-all duration-500 border border-gray-100 dark:border-gray-700 overflow-hidden h-full">
                          {/* Image Section */}
                          <div className="relative h-64 overflow-hidden">
                            <img
                              src={image}
                              alt={translation?.title || "Project"}
                              className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-110"
                            />
                            <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-black/20 to-transparent"></div>

                            {/* Status Badge */}
                            <div className="absolute top-4 left-4">
                              <span
                                className={`inline-flex items-center px-3 py-1.5 rounded-full text-xs font-bold text-white backdrop-blur-sm border ${project.status === "planning"
                                  ? "bg-yellow-500/90 border-yellow-400/30"
                                  : project.status === "in_progress"
                                    ? "bg-blue-500/90 border-blue-400/30"
                                    : project.status === "completed"
                                      ? "bg-green-500/90 border-green-400/30"
                                      : "bg-red-500/90 border-red-400/30"
                                  }`}
                              >
                                {t(`projects.status.${project.status}`)}
                              </span>
                            </div>

                            {/* Floating Stat Card */}
                            <motion.div
                              initial={{ opacity: 0, y: 20 }}
                              animate={isDataLoaded ? { opacity: 1, y: 0 } : { opacity: 0, y: 20 }}
                              transition={{ duration: 0.6, delay: 0.4 + index * 0.1 }}
                              className="absolute bottom-4 right-4 bg-white/90 dark:bg-gray-900/90 backdrop-blur-md rounded-xl p-3 shadow-lg border border-white/50 dark:border-gray-700/50"
                            >
                              <div className="text-lg font-bold text-gray-900 dark:text-white">
                                {Math.round(progressPercentage)}%
                              </div>
                              <div className="text-xs text-gray-600 dark:text-gray-400 font-medium">
                                {t("projects.funded", "Funded")}
                              </div>
                            </motion.div>

                            {/* Title Overlay */}
                            <div className="absolute bottom-4 left-4 right-20">
                              <h3 className="text-xl font-bold text-white mb-1 line-clamp-2 drop-shadow-lg">
                                {translation?.title || "Untitled Project"}
                              </h3>
                            </div>
                          </div>

                          {/* Content Section */}
                          <div className="p-6">
                            <p className="text-gray-600 dark:text-gray-400 text-sm leading-relaxed mb-6 line-clamp-3">
                              {translation?.description || ""}
                            </p>

                            {/* Funding Progress */}
                            <div className="mb-6">
                              <div className="flex justify-between items-center mb-3">
                                <span className="text-sm font-semibold text-gray-900 dark:text-white">
                                  {t("projects.fundingProgress", "Funding Progress")}
                                </span>
                                <span className="text-sm font-bold text-amber-600 dark:text-amber-500">
                                  {Math.round(progressPercentage)}%
                                </span>
                              </div>
                              <div className="w-full bg-gray-200 dark:bg-gray-700 rounded-full h-3 overflow-hidden mb-2">
                                <motion.div
                                  initial={{ width: 0 }}
                                  animate={
                                    isDataLoaded
                                      ? { width: `${progressPercentage}%` }
                                      : { width: 0 }
                                  }
                                  transition={{
                                    duration: 1.5,
                                    delay: 0.6 + index * 0.1,
                                    ease: "easeOut",
                                  }}
                                  className="bg-gradient-to-r from-emerald-500 via-emerald-500 to-stone-500 h-full rounded-full relative overflow-hidden"
                                >
                                  <div className="absolute inset-0 bg-white/30 animate-pulse"></div>
                                </motion.div>
                              </div>
                              <div className="flex justify-between items-center text-xs text-gray-600 dark:text-gray-400">
                                <span className="font-medium">
                                  {project.currency} {project.raised_amount.toLocaleString()}
                                </span>
                                <span className="font-medium">
                                  {project.currency} {remaining.toLocaleString()} {t("projects.remaining", "remaining")}
                                </span>
                              </div>
                              <div className="mt-2 text-xs text-gray-500 dark:text-gray-500 text-center font-medium">
                                {t("projects.goal", "Goal")}: {project.currency} {project.goal_amount.toLocaleString()}
                              </div>
                            </div>

                            {/* Action Buttons */}
                            <div className="flex flex-col gap-3">
                              <motion.button
                                whileHover={{ scale: 1.02, y: -2 }}
                                whileTap={{ scale: 0.98 }}
                                onClick={() =>
                                  navigate(`/${currentLanguage}/projects/${project.id}`)
                                }
                                className="w-full inline-flex items-center justify-center gap-2 px-6 py-3 bg-white dark:bg-gray-800 hover:bg-gray-50 dark:hover:bg-gray-700 text-gray-900 dark:text-white rounded-2xl border-2 border-gray-200 dark:border-gray-700 font-semibold transition-all duration-300 text-base shadow-md hover:shadow-lg"
                              >
                                {t("projects.viewDetails", "View Details")}
                                <svg
                                  className="w-5 h-5"
                                  fill="none"
                                  stroke="currentColor"
                                  viewBox="0 0 24 24"
                                >
                                  <path
                                    strokeLinecap="round"
                                    strokeLinejoin="round"
                                    strokeWidth={2}
                                    d="M9 5l7 7-7 7"
                                  />
                                </svg>
                              </motion.button>
                              <motion.button
                                whileHover={{ scale: 1.02, y: -2 }}
                                whileTap={{ scale: 0.98 }}
                                onClick={() => {
                                  setSelectedProject({ id: project.id, title: translation?.title || "Project" });
                                  setIsPaymentModalOpen(true);
                                }}
                                className="w-full inline-flex items-center justify-center gap-2 px-6 py-3 bg-gradient-to-r from-emerald-600 to-stone-600 hover:from-emerald-700 hover:to-stone-700 text-white rounded-2xl font-bold transition-all duration-300 text-base shadow-lg hover:shadow-xl"
                              >
                                <span className="text-lg">💚</span>
                                {t("projects.contribute", "Contribute")}
                              </motion.button>
                            </div>

                            {project.featured && (
                              <div className="mt-4 text-center">
                                <span className="inline-flex items-center gap-1 px-3 py-1 bg-amber-100 dark:bg-amber-900/30 text-amber-800 dark:text-amber-300 rounded-full text-xs font-semibold">
                                  <span>⭐</span>
                                  {t("projects.featured", "Featured")}
                                </span>
                              </div>
                            )}
                          </div>
                        </div>
                      </motion.div>
                    );
                  })}
                </div>

                {/* Pagination */}
                {pagination && pagination.last_page > 1 && (
                  <motion.div
                    initial={{ opacity: 0, y: 30 }}
                    animate={isDataLoaded ? { opacity: 1, y: 0 } : { opacity: 0, y: 30 }}
                    transition={{ duration: 0.6, delay: 0.8 }}
                    className="flex justify-center items-center mt-16 gap-2"
                  >
                    <motion.button
                      whileHover={{ scale: 1.05, y: -2 }}
                      whileTap={{ scale: 0.95 }}
                      onClick={() => setCurrentPage(Math.max(1, currentPage - 1))}
                      disabled={currentPage === 1}
                      className="px-5 py-3 bg-white dark:bg-gray-800 text-gray-700 dark:text-gray-300 rounded-xl disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-50 dark:hover:bg-gray-700 transition-all duration-300 shadow-md hover:shadow-lg border border-gray-200 dark:border-gray-700 font-semibold disabled:hover:scale-100 disabled:hover:shadow-md"
                    >
                      <svg
                        className="w-5 h-5"
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d="M15 19l-7-7 7-7"
                        />
                      </svg>
                    </motion.button>

                    <div className="flex gap-2">
                      {Array.from({ length: pagination.last_page }, (_, i) => i + 1)
                        .filter((page) => {
                          const start = Math.max(1, currentPage - 2);
                          const end = Math.min(pagination.last_page, currentPage + 2);
                          return page >= start && page <= end;
                        })
                        .map((page) => (
                          <motion.button
                            key={page}
                            whileHover={{ scale: 1.1, y: -2 }}
                            whileTap={{ scale: 0.95 }}
                            onClick={() => setCurrentPage(page)}
                            className={`px-5 py-3 rounded-xl font-semibold transition-all duration-300 ${page === currentPage
                              ? "bg-gradient-to-r from-amber-500 to-orange-600 text-white shadow-lg"
                              : "bg-white dark:bg-gray-800 text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700 shadow-md hover:shadow-lg border border-gray-200 dark:border-gray-700"
                              }`}
                          >
                            {page}
                          </motion.button>
                        ))}
                    </div>

                    <motion.button
                      whileHover={{ scale: 1.05, y: -2 }}
                      whileTap={{ scale: 0.95 }}
                      onClick={() =>
                        setCurrentPage(Math.min(pagination.last_page, currentPage + 1))
                      }
                      disabled={currentPage === pagination.last_page}
                      className="px-5 py-3 bg-white dark:bg-gray-800 text-gray-700 dark:text-gray-300 rounded-xl disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-50 dark:hover:bg-gray-700 transition-all duration-300 shadow-md hover:shadow-lg border border-gray-200 dark:border-gray-700 font-semibold disabled:hover:scale-100 disabled:hover:shadow-md"
                    >
                      <svg
                        className="w-5 h-5"
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d="M9 5l7 7-7 7"
                        />
                      </svg>
                    </motion.button>
                  </motion.div>
                )}
              </>
            )}
          </div>
        </section>

        {/* Call to Action */}
        <section className="relative py-24 px-4 sm:px-6 lg:px-8 overflow-hidden bg-gradient-to-br from-emerald-600 via-stone-600 to-emerald-700 text-white">
          {/* Background decorative elements */}
          <div className="absolute inset-0 overflow-hidden pointer-events-none">
            <div className="absolute top-0 right-0 w-96 h-96 bg-white/10 rounded-full blur-3xl -translate-y-1/2 translate-x-1/2"></div>
            <div className="absolute bottom-0 left-0 w-96 h-96 bg-white/10 rounded-full blur-3xl translate-y-1/2 -translate-x-1/2"></div>
          </div>

          <div
            className="absolute inset-0 opacity-30"
            style={{
              backgroundImage: `url("data:image/svg+xml,%3Csvg width='100' height='100' viewBox='0 0 100 100' xmlns='http://www.w3.org/2000/svg'%3E%3Cpath d='M11 18c3.866 0 7-3.134 7-7s-3.134-7-7-7-7 3.134-7 7 3.134 7 7 7zm48 25c3.866 0 7-3.134 7-7s-3.134-7-7-7-7 3.134-7 7 3.134 7 7 7zm-43-7c1.657 0 3-1.343 3-3s-1.343-3-3-3-3 1.343-3 3 1.343 3 3 3zm63 31c1.657 0 3-1.343 3-3s-1.343-3-3-3-3 1.343-3 3 1.343 3 3 3zM34 90c1.657 0 3-1.343 3-3s-1.343-3-3-3-3 1.343-3 3 1.343 3 3 3zm56-76c1.657 0 3-1.343 3-3s-1.343-3-3-3-3 1.343-3 3 1.343 3 3 3zM12 86c2.21 0 4-1.79 4-4s-1.79-4-4-4-4 1.79-4 4 1.79 4 4 4zm28-65c2.21 0 4-1.79 4-4s-1.79-4-4-4-4 1.79-4 4 1.79 4 4 4zm23-11c2.76 0 5-2.24 5-5s-2.24-5-5-5-5 2.24-5 5 2.24 5 5 5zm-6 60c2.21 0 4-1.79 4-4s-1.79-4-4-4-4 1.79-4 4 1.79 4 4 4zm29 22c2.76 0 5-2.24 5-5s-2.24-5-5-5-5 2.24-5 5 2.24 5 5 5zM32 63c2.76 0 5-2.24 5-5s-2.24-5-5-5-5 2.24-5 5 2.24 5 5 5zm57-13c2.76 0 5-2.24 5-5s-2.24-5-5-5-5 2.24-5 5 2.24 5 5 5zm-9-21c1.105 0 2-.895 2-2s-.895-2-2-2-2 .895-2 2 .895 2 2 2zM60 91c1.105 0 2-.895 2-2s-.895-2-2-2-2 .895-2 2 .895 2 2 2zM35 41c1.105 0 2-.895 2-2s-.895-2-2-2-2 .895-2 2 .895 2 2 2zM12 60c1.105 0 2-.895 2-2s-.895-2-2-2-2 .895-2 2 .895 2 2 2z' fill='%23ffffff' fill-opacity='0.05' fill-rule='evenodd'/%3E%3C/svg%3E")`,
            }}
          ></div>

          <div className="relative z-10 max-w-4xl mx-auto text-center">
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              animate={isDataLoaded ? { opacity: 1, y: 0 } : { opacity: 0, y: 30 }}
              transition={{ duration: 0.8, delay: 0.2 }}
            >
              <motion.div
                initial={{ scale: 0.9, opacity: 0 }}
                animate={isDataLoaded ? { scale: 1, opacity: 1 } : { scale: 0.9, opacity: 0 }}
                transition={{ duration: 0.6, delay: 0.4 }}
                className="mb-6"
              >
                <span className="text-6xl">🤝</span>
              </motion.div>
              <h2 className="text-4xl md:text-5xl lg:text-6xl font-extrabold mb-6 leading-tight">
                {t("projects.cta.title", "Your Support Makes the Difference")}
              </h2>
              <p className="text-xl md:text-2xl mb-10 opacity-95 font-light leading-relaxed max-w-3xl mx-auto">
                {t("projects.cta.description", "Join our community of changemakers. Every contribution, big or small, helps build a better Djichami.")}
              </p>
              <motion.button
                whileHover={{ scale: 1.05, y: -2 }}
                whileTap={{ scale: 0.95 }}
                onClick={() => {
                  // Scroll to top or navigate to projects
                  window.scrollTo({ top: 0, behavior: "smooth" });
                }}
                className="inline-flex items-center gap-2 px-10 py-5 bg-white text-emerald-600 rounded-2xl font-bold text-lg shadow-2xl hover:bg-gray-50 transition-all duration-300 border-2 border-transparent hover:border-white/50"
              >
                <span>{t("projects.cta.button", "Start Supporting Today")}</span>
                <span className="text-xl">🎯</span>
              </motion.button>
            </motion.div>
          </div>
        </section>
      </main>
      <Footer />
      <BackToTop />

      <PaymentModal
        isOpen={isPaymentModalOpen}
        onClose={() => setIsPaymentModalOpen(false)}
        projectTitle={selectedProject?.title}
      />
    </div>
  );
};

export default Projects;